import React, { useMemo, useRef, useState, useEffect } from 'react';
import { computeValue } from '../utils/composite';
import SelkirkIcon from './icons/SelkirkIcon';
import { ICON } from '../icons/iconIds';
import './IntervalStrip.css';

const MIN_BAR_PX = 14;
const HEADER_OFFSET_PX = 40;

const IntervalStrip = ({
  rows,
  selection,
  range,
  pxPerMeter,
  barScale,
  selectedIds,
  economicConfig,
  rockColors,
  viewMode = 'cueq',
  onSelectionChange
}) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const headerRef = useRef(null);
  
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [hover, setHover] = useState(null);
  const [isClick, setIsClick] = useState(false);
  const [cutoffCuEq, setCutoffCuEq] = useState(0.3);
  const [cutoffCu, setCutoffCu] = useState(0.2);
  const [cutoffAu, setCutoffAu] = useState(0.5);
  const [cutoffAg, setCutoffAg] = useState(5);
  const [draggingCutoff, setDraggingCutoff] = useState(false);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => a.from_depth - b.from_depth),
    [rows]
  );

  const safeDivide = (num, den) => (den === 0 ? 0 : num / den);

  const maxima = useMemo(() => {
    return {
      cu: Math.max(...sortedRows.map((r) => r.cu_best), 0),
      au: Math.max(...sortedRows.map((r) => r.au_best), 0),
      ag: Math.max(...sortedRows.map((r) => r.ag_best), 0)
    };
  }, [sortedRows]);

  const cuEqPerRow = useMemo(() => {
    const oxideFactor = Math.max(0, 1 - economicConfig.oxideRatioPct / 100);
    const cuPayRec =
      economicConfig.payable.cu *
      Math.min(economicConfig.recovery.cu, economicConfig.cappedRecovery.cu) *
      oxideFactor;
    const cuDenom =
      economicConfig.conversion.cuLbPerTonne *
      economicConfig.prices.cuPerLb *
      cuPayRec;

    const calc = (cu, au, ag) => {
      const val = computeValue({ cu, au, ag }, economicConfig);
      const gross = val.valueCuUsdPerT + val.valueAuUsdPerT + val.valueAgUsdPerT;
      const total = gross > 0 ? gross : 1;
      const grossCuEq = safeDivide(gross, cuDenom);
      return {
        cuEq: Math.max(val.cuEqPct, grossCuEq),
        shareCu: val.valueCuUsdPerT / total,
        shareAu: val.valueAuUsdPerT / total,
        shareAg: val.valueAgUsdPerT / total
      };
    };

    const list = sortedRows.map((r) => {
      const actual = calc(r.cu_best, r.au_best, r.ag_best);
      // Expected CuEq from block model grades (same economic config as actual)
      const expected =
        viewMode === 'blockModel' && r.hasBlockData
          ? calc(r.cu_block, r.au_block, r.ag_block)
          : null;
      return { id: r.sample_number, ...actual, expected };
    });
    // Shared scale across actual and expected so both slots are comparable
    const maxCuEq = Math.max(
      ...list.map((v) => Math.max(v.cuEq, v.expected?.cuEq ?? 0)),
      0.0001
    );
    const map = new Map();
    list.forEach((item) => {
      map.set(item.id, { ...item, maxCuEq });
    });
    return map;
  }, [sortedRows, economicConfig, viewMode]);

  if (!range) {
    return (
      <div className="interval-strip empty">
        <p>Load data to view drill string.</p>
      </div>
    );
  }

  const height = Math.max(
    (range.maxTo - range.minFrom) * pxPerMeter,
    MIN_BAR_PX * sortedRows.length + 40
  );

  const depthToY = (depth) => (depth - range.minFrom) * pxPerMeter;
  
  const yToDepth = (clientY) => {
    const container = containerRef.current;
    if (!container) return range.minFrom;
    const rect = container.getBoundingClientRect();
    const headerH = headerRef.current?.offsetHeight ?? HEADER_OFFSET_PX;
    const y = clientY - rect.top + container.scrollTop - headerH;
    const depth = range.minFrom + Math.max(0, y) / pxPerMeter;
    return Math.min(Math.max(depth, range.minFrom), range.maxTo);
  };

  const snapDepth = (depth) => {
    if (!sortedRows.length) return depth;
    let best = depth;
    let bestDelta = Infinity;
    for (const r of sortedRows) {
      const candidates = [r.from_depth, r.to_depth];
      for (const c of candidates) {
        const d = Math.abs(c - depth);
        if (d < bestDelta) {
          bestDelta = d;
          best = c;
        }
      }
    }
    return best;
  };

  const rowAtDepth = (depth, rowsList) => {
    if (!rowsList.length) return null;
    const direct = rowsList.find((r) => depth >= r.from_depth && depth <= r.to_depth);
    if (direct) return direct;
    let best = null;
    let bestDelta = Infinity;
    for (const r of rowsList) {
      const d = Math.min(Math.abs(r.from_depth - depth), Math.abs(r.to_depth - depth));
      if (d < bestDelta) {
        bestDelta = d;
        best = r;
      }
    }
    return best;
  };

  const handleMouseDown = (e) => {
    const d = yToDepth(e.clientY);
    setDragging(true);
    setDragStart(d);
    setDragCurrent(d);
    setIsClick(true);
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    const next = yToDepth(e.clientY);
    setDragCurrent(next);
    if (Math.abs(depthToY(next) - depthToY(dragStart ?? next)) > 3) {
      setIsClick(false);
    }
  };

  const handleMouseUp = (e) => {
    if (!dragging) return;
    const end = yToDepth(e.clientY);
    const start = dragStart ?? end;
    setDragging(false);
    setDragStart(null);
    setDragCurrent(null);
    
    if (isClick) {
      const hit = rowAtDepth(start, sortedRows);
      if (hit) {
        onSelectionChange(hit.from_depth, hit.to_depth, {
          toggleSampleId: hit.sample_number
        });
      }
    } else {
      const snappedStart = snapDepth(start);
      const snappedEnd = snapDepth(end);
      const min = Math.min(snappedStart, snappedEnd);
      const max = Math.max(snappedStart, snappedEnd);
      const inRange = sortedRows
        .filter((r) => r.from_depth >= min && r.to_depth <= max)
        .map((r) => r.sample_number);
      onSelectionChange(min, max, { extend: e.shiftKey, rangeIds: inRange });
    }
    setIsClick(false);
  };

  const overlayStart = dragStart;
  const overlayEnd = dragCurrent;
  const overlayTop =
    overlayStart !== null && overlayEnd !== null ? depthToY(Math.min(overlayStart, overlayEnd)) : 0;
  const overlayHeight =
    overlayStart !== null && overlayEnd !== null
      ? Math.abs(depthToY(overlayEnd) - depthToY(overlayStart))
      : 0;

  const maxCuEqValue = Math.max(
    ...sortedRows.map((r) => {
      const info = cuEqPerRow.get(r.sample_number);
      return Math.max(info?.cuEq ?? 0, info?.expected?.cuEq ?? 0);
    }),
    0.01
  );
  const isBlockView = viewMode === 'blockModel';

  return (
    <div className="interval-strip" ref={containerRef}>
      <div
        className="interval-track"
        style={{ height }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        ref={trackRef}
      >
        <div className="interval-header" ref={headerRef}>
          <div>Depth (m)</div>
          <div className="lith-header">Lith</div>
          {isBlockView ? (
            <div className="bars header-bars dual-headers">
              {[
                { key: 'actual', label: 'CuEq Actual', cls: 'actual-header' },
                { key: 'expected', label: 'CuEq Expected (Block)', cls: 'expected-header' }
              ].map((slot) => (
                <div key={slot.key} className={`dual-header-group ${slot.cls}`}>
                  <span className="metal-label">{slot.label}</span>
                  <div
                    className="cutoff-control"
                    draggable
                    onDragStart={(e) => {
                      setDraggingCutoff(true);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDrag={(e) => {
                      if (e.clientX > 0) {
                        const group = e.currentTarget.parentElement;
                        const rect = group.getBoundingClientRect();
                        const x = Math.max(0, e.clientX - rect.left);
                        const rawCutoff = Math.max(
                          0,
                          Math.min(maxCuEqValue, ((x / rect.width) * maxCuEqValue) / barScale)
                        );
                        const snappedCutoff = Math.round(rawCutoff / 0.05) * 0.05;
                        setCutoffCuEq(Number(snappedCutoff.toFixed(2)));
                      }
                    }}
                    onDragEnd={() => setDraggingCutoff(false)}
                    title="Drag to adjust cutoff (applies to both slots)"
                  >
                    <SelkirkIcon name={ICON.uiSortNeutral} size={12} decorative />{' '}
                    {cutoffCuEq.toFixed(2)}% CuEq
                  </div>
                </div>
              ))}
            </div>
          ) : viewMode === 'cueq' ? (
            <div className="bars header-bars">
              <div 
                className="cutoff-control"
                draggable
                onDragStart={(e) => {
                  setDraggingCutoff(true);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDrag={(e) => {
                  if (e.clientX > 0) {
                    const barsArea = e.currentTarget.parentElement;
                    if (barsArea) {
                      const rect = barsArea.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const rawCutoff = Math.max(0, Math.min(maxCuEqValue, (x / rect.width) * maxCuEqValue));
                      const snappedCutoff = Math.round(rawCutoff / 0.05) * 0.05;
                      setCutoffCuEq(Number(snappedCutoff.toFixed(2)));
                    }
                  }
                }}
                onDragEnd={() => setDraggingCutoff(false)}
                title="Drag to adjust cutoff"
              >
                <SelkirkIcon name={ICON.uiSortNeutral} size={12} decorative />{' '}
                {cutoffCuEq.toFixed(2)}% CuEq
              </div>
            </div>
          ) : (
            <div className="bars header-bars individual-headers">
              <div className="individual-header-group cu-header">
                <span className="metal-label cu-label">Cu %</span>
                <div 
                  className="cutoff-control cu-cutoff"
                  draggable
                  onDragStart={(e) => {
                    setDraggingCutoff(true);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDrag={(e) => {
                    if (e.clientX > 0) {
                      const headerGroup = e.currentTarget.parentElement;
                      const rect = headerGroup.getBoundingClientRect();
                      const x = Math.max(0, e.clientX - rect.left);
                      const maxCu = maxima.cu;
                      const rawCutoff = Math.max(0, Math.min(maxCu, (x / rect.width) * maxCu / barScale));
                      const snappedCutoff = Math.round(rawCutoff / 0.05) * 0.05;
                      setCutoffCu(Number(snappedCutoff.toFixed(2)));
                    }
                  }}
                  onDragEnd={() => setDraggingCutoff(false)}
                  title="Drag to adjust Cu cutoff"
                >
                  <SelkirkIcon name={ICON.uiSortNeutral} size={12} decorative /> {cutoffCu.toFixed(2)}
                </div>
              </div>
              <div className="individual-header-group au-header">
                <span className="metal-label au-label">Au g/t</span>
                <div 
                  className="cutoff-control au-cutoff"
                  draggable
                  onDragStart={(e) => {
                    setDraggingCutoff(true);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDrag={(e) => {
                    if (e.clientX > 0) {
                      const headerGroup = e.currentTarget.parentElement;
                      const rect = headerGroup.getBoundingClientRect();
                      const x = Math.max(0, e.clientX - rect.left);
                      const maxAu = maxima.au;
                      const rawCutoff = Math.max(0, Math.min(maxAu, (x / rect.width) * maxAu / barScale));
                      const snappedCutoff = Math.round(rawCutoff / 0.1) * 0.1;
                      setCutoffAu(Number(snappedCutoff.toFixed(1)));
                    }
                  }}
                  onDragEnd={() => setDraggingCutoff(false)}
                  title="Drag to adjust Au cutoff"
                >
                  <SelkirkIcon name={ICON.uiSortNeutral} size={12} decorative /> {cutoffAu.toFixed(1)}
                </div>
              </div>
              <div className="individual-header-group ag-header">
                <span className="metal-label ag-label">Ag g/t</span>
                <div 
                  className="cutoff-control ag-cutoff"
                  draggable
                  onDragStart={(e) => {
                    setDraggingCutoff(true);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDrag={(e) => {
                    if (e.clientX > 0) {
                      const headerGroup = e.currentTarget.parentElement;
                      const rect = headerGroup.getBoundingClientRect();
                      const x = Math.max(0, e.clientX - rect.left);
                      const maxAg = maxima.ag;
                      const rawCutoff = Math.max(0, Math.min(maxAg, (x / rect.width) * maxAg / barScale));
                      const snappedCutoff = Math.round(rawCutoff / 1) * 1;
                      setCutoffAg(Number(snappedCutoff.toFixed(0)));
                    }
                  }}
                  onDragEnd={() => setDraggingCutoff(false)}
                  title="Drag to adjust Ag cutoff"
                >
                  <SelkirkIcon name={ICON.uiSortNeutral} size={12} decorative /> {cutoffAg.toFixed(0)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {sortedRows.map((row) => {
          const y = depthToY(row.from_depth);
          const h = Math.max(MIN_BAR_PX, depthToY(row.to_depth) - y);
          const rockColor = rockColors?.[row.rock_type] ?? '#4b5563';
          const cuEqInfo = cuEqPerRow.get(row.sample_number);
          const maxCuEq = cuEqInfo?.maxCuEq ?? 1;
          const shareCu = cuEqInfo?.shareCu ?? 0;
          const shareAu = cuEqInfo?.shareAu ?? 0;
          const shareAg = cuEqInfo?.shareAg ?? 0;
          const cuEq = cuEqInfo?.cuEq ?? 0;
          const pctWidth = Math.min(100, (cuEq / maxCuEq) * 100 * barScale);
          const pCu = shareCu * 100;
          const pAu = shareAu * 100;
          const pAg = shareAg * 100;
          const isSelected = selectedIds.has(row.sample_number);
          
          return (
            <div
              className="interval-row"
              key={`${row.sample_number}-${row.from_depth}`}
              style={{ top: y, height: h, background: `${rockColor}1a` }}
              onMouseEnter={(e) => setHover({ row, y: e.clientY })}
              onMouseLeave={() => setHover(null)}
            >
              <div
                className="interval-depths"
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onMouseMove={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(row.sample_number)}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelectionChange(row.from_depth, row.to_depth, {
                      toggleSampleId: row.sample_number
                    });
                  }}
                />
                <span className="depth-label">
                  {row.from_depth.toFixed(2)}–{row.to_depth.toFixed(2)}
                </span>
              </div>
              <div 
                className="lith-bar" 
                style={{ background: rockColor }}
                title={row.rock_type}
              >
                <span className="lith-label">{row.rock_type}</span>
              </div>
              {isBlockView ? (
                <div className="bars dual-bars">
                  <div className="dual-column actual-column">
                    <div
                      className="bar"
                      style={{
                        width: `${pctWidth}%`,
                        background: `linear-gradient(90deg,
                        #b87333 0%,
                        #b87333 ${pCu}%,
                        #d4af37 ${pCu}%,
                        #d4af37 ${pCu + pAu}%,
                        #c0c0c0 ${pCu + pAu}%,
                        #c0c0c0 100%)`,
                        opacity: isSelected ? 1 : 0.85
                      }}
                      title={`Actual CuEq ${cuEq.toFixed(3)}% | Cu ${row.cu_best} Au ${row.au_best} Ag ${row.ag_best}`}
                    />
                    <span className="bar-label">{cuEq.toFixed(3)}%</span>
                    <div
                      className="slot-cutoff-line"
                      style={{ left: `${Math.min(100, (cutoffCuEq / maxCuEq) * 100 * barScale)}%` }}
                    />
                  </div>
                  <div className="dual-column expected-column">
                    {cuEqInfo?.expected ? (
                      <>
                        <div
                          className="bar expected-bar"
                          style={{
                            width: `${Math.min(100, (cuEqInfo.expected.cuEq / maxCuEq) * 100 * barScale)}%`,
                            background: 'linear-gradient(to right, #10b981 0%, #3b82f6 100%)',
                            opacity: isSelected ? 1 : 0.85
                          }}
                          title={`Expected CuEq ${cuEqInfo.expected.cuEq.toFixed(3)}% | Cu ${row.cu_block} Au ${row.au_block} Ag ${row.ag_block}`}
                        />
                        <span className="bar-label">{cuEqInfo.expected.cuEq.toFixed(3)}%</span>
                      </>
                    ) : row.hasBlockData === false ? (
                      <span className="no-block-data" title="No block model data for this sample">
                        ⚠ no block data
                      </span>
                    ) : null}
                    <div
                      className="slot-cutoff-line"
                      style={{ left: `${Math.min(100, (cutoffCuEq / maxCuEq) * 100 * barScale)}%` }}
                    />
                  </div>
                </div>
              ) : viewMode === 'cueq' ? (
                <div className="bars">
                  <div
                    className="bar"
                    style={{
                      width: `${pctWidth}%`,
                      background: `linear-gradient(90deg,
                        #b87333 0%,
                        #b87333 ${pCu}%,
                        #d4af37 ${pCu}%,
                        #d4af37 ${pCu + pAu}%,
                        #c0c0c0 ${pCu + pAu}%,
                        #c0c0c0 100%)`,
                      opacity: isSelected ? 1 : 0.85
                    }}
                    title={`CuEq ${cuEq.toFixed(3)}% | Cu ${row.cu_best} Au ${row.au_best} Ag ${row.ag_best}`}
                  />
                  <span className="bar-label">{cuEq.toFixed(3)}%</span>
                </div>
              ) : (
                <div className="bars individual-bars">
                  <div className="individual-column cu-column">
                    <div 
                      className="individual-bar cu-bar"
                      style={{
                        width: `${Math.min(100, (row.cu_best / maxima.cu) * 100 * barScale)}%`,
                        background: '#b87333'
                      }}
                      title={`Cu: ${row.cu_best}%`}
                    />
                  </div>
                  <div className="individual-column au-column">
                    <div 
                      className="individual-bar au-bar"
                      style={{
                        width: `${Math.min(100, (row.au_best / maxima.au) * 100 * barScale)}%`,
                        background: '#d4af37'
                      }}
                      title={`Au: ${row.au_best} g/t`}
                    />
                  </div>
                  <div className="individual-column ag-column">
                    <div 
                      className="individual-bar ag-bar"
                      style={{
                        width: `${Math.min(100, (row.ag_best / maxima.ag) * 100 * barScale)}%`,
                        background: '#c0c0c0'
                      }}
                      title={`Ag: ${row.ag_best} g/t`}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {isBlockView ? null : viewMode === 'cueq' ? (
          <div
            className="cutoff-line"
            style={{
              left: `${(cutoffCuEq / maxCuEqValue) * 100 * barScale}%`
            }}
          />
        ) : (
          <>
            <div
              className="cutoff-line cu-cutoff-line"
              style={{
                left: `calc(${(cutoffCu / maxima.cu) * 33.33 * barScale}%)`
              }}
            />
            <div
              className="cutoff-line au-cutoff-line"
              style={{
                left: `calc(33.33% + ${(cutoffAu / maxima.au) * 33.33 * barScale}%)`
              }}
            />
            <div
              className="cutoff-line ag-cutoff-line"
              style={{
                left: `calc(66.66% + ${(cutoffAg / maxima.ag) * 33.33 * barScale}%)`
              }}
            />
          </>
        )}
        
        <div
          className="selection-overlay"
          style={{
            display: dragging ? 'block' : 'none',
            top: overlayTop,
            height: overlayHeight
          }}
        />
        
        <div
          className="selection-current"
          style={{
            top: depthToY(selection.from),
            height: Math.max(2, depthToY(selection.to) - depthToY(selection.from))
          }}
        />
        
        <div className="selection-line" style={{ top: depthToY(selection.from) }} />
        <div className="selection-line" style={{ top: depthToY(selection.to) }} />
        
        {hover && (
          <div
            className="tooltip"
            style={{
              top:
                hover.y -
                (containerRef.current?.getBoundingClientRect().top ?? 0) +
                containerRef.current.scrollTop
            }}
          >
            <div className="tooltip-row">
              <strong>{hover.row.sample_number}</strong> · {hover.row.rock_type}
            </div>
            <div className="tooltip-row">
              {hover.row.from_depth.toFixed(2)}–{hover.row.to_depth.toFixed(2)} m
            </div>
            <div className="tooltip-row">
              Cu {hover.row.cu_best.toFixed(3)}% · Au {hover.row.au_best.toFixed(3)} g/t · Ag {hover.row.ag_best.toFixed(2)} g/t
            </div>
            {isBlockView && (
              hover.row.hasBlockData ? (
                <>
                  <div className="tooltip-row">
                    Block: Cu {hover.row.cu_block.toFixed(3)}% · Au {hover.row.au_block.toFixed(3)} g/t · Ag {hover.row.ag_block.toFixed(2)} g/t
                  </div>
                  <div className="tooltip-row">
                    Expected CuEq {(cuEqPerRow.get(hover.row.sample_number)?.expected?.cuEq ?? 0).toFixed(3)}%
                    {' · '}Zone {hover.row.block_zone ?? '-'} · Class {hover.row.block_class ?? '-'}
                  </div>
                  <div className="tooltip-row">
                    Recov Cu {hover.row.recov_cu}% · Au {hover.row.recov_au}% · Ag {hover.row.recov_ag}%
                  </div>
                </>
              ) : (
                <div className="tooltip-row">⚠ No block model data for this sample</div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntervalStrip;

